<template>
  <BaseModal
    :actions="true"
    :modifiers="['flat-title']"
    :title="$t('issues.bulk_import')"
    @dismiss-modal="$emit('dismissModal', false)"
  >
    <template #content>
      <div
        :class="{
          'issue__section issue__section-document issue__section--light': true,
          'issue__section--error': !isValidFileType || (!isValidDocument && documentList.length > 0),
        }"
      >
        <div
          class="issue-field"
        >
          <div class="issue-field__wrapper">
            <button
              aria-label="bulk_import"
              class="issue-field__menu issue-field__menu--always-show"
              type="button"
              @click="newFile"
            >
              <PlusIcon
                class="icon icon--small"
              />
              <input
                id="attachment-upload"
                ref="fileInput"
                :acceptedExcelTypes="acceptedExcelTypes"
                class="hide"
                multiple="false"
                type="file"
                @change="addFiles"
              >
            </button>
            <div class="issue-field__header">
              <div
                id="document-count"
                class="issue-field__title typo-small-title"
              >
                {{
                  pluralize(
                    documentList.length,
                    $t('issues.document'),
                    $t('issues.documents')
                  )
                }}
              </div>
            </div>

            <div class="issue__attachments">
              <div
                v-for="document in documentList"
                :key="document"
                class="issue__attachment"
              >
                <div class="issue__attachment-icon">
                  <FileIcon :contentType="document.content_type" />
                </div>
                <div class="issue__attachment-name">
                  {{ document.name }}
                </div>
                <div class="issue__attachment-file-size typo-tiny">
                  {{ document.size }}
                </div>
              </div>
              <div
                v-if="documentList.length === 0"
                class="issue-field__body"
              >
                <p class="typo-small-title">
                  {{ $t('issues.no_documents') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="!isValidDocument && documentList.length > 0"
        class="issue-field__text issue-field__text--error"
      >
        <p class="typo-ignore typo-small">
          {{ $t('issues.invalid_document.message') }}
        </p>
        <p class="typo-ignore typo-small">
          {{ $t('issues.invalid_document.question') }}
        </p>
      </div>

      <div class="issue-field__text typo-small">
        {{ $t('issues.import_description') }}
        <a
          class="issue-field__link"
          :href="downloadTemplatePath"
          target="_blank"
        >
          {{ $t('issues.import_template') }}
        </a>
      </div>
    </template>
    <template #actions>
      <FormSubmitButton
        :buttonLabel="$t('issues.upload_data')"
        :disabled="!isValidDocument || !isValidFileType || documentList.length <= 0"
        :isSaving="saving"
        @click="uploadTable"
      />
    </template>
  </BaseModal>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';

// Utils
import { acceptedExcelTypes } from '@/config/accepted_file_types';

// Components
import BaseModal from '@/components/generic/base_modal.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import PlusIcon from '@/images/plus-16.svg';
import pluralize from '@/functions/pluralize';
import FileIcon from '@/components/generic/file_icon.vue';
import { bulkImport, validateFileType } from '@/functions/excel_import';
import { useBulkImportStore } from '@/stores/bulk_import/bulkImportStore';

const emit = defineEmits(['dismissModal']);

const router = useRouter();
const saving = ref(false);
const isValidFileType = ref(true);
const isValidDocument = ref(true);
const fileInput = ref(null);
const importedData = ref([]);
const documentList = ref([]);
const bulkImportStore = useBulkImportStore();
const downloadTemplatePath = ref('/eaimms-bulk-issue-upload-template.xlsx');

function newFile() {
  fileInput.value.focus();
  fileInput.value.click();
}

function addFiles({ target: { files } }) {
  if (!files || files.length <= 0) return;
  Object.values(files).forEach((attachment) => addAttachment(attachment));
}

async function addAttachment(file) {
  documentList.value = [];
  if (!file) return;
  isValidFileType.value = validateFileType(acceptedExcelTypes, file.type);

  if (!isValidFileType.value) return isValidDocument.value = false;
  documentList.value = [file];

  await bulkImport(file, (result) => {
    importedData.value = result?.data;
    isValidDocument.value = result?.valid;
  });
}

function uploadTable() {
  saving.value = !saving.value;
  bulkImportStore.$patch({ tableData: importedData.value });
  emit('dismissModal', false);
  documentList.value = [];
  saving.value = !saving.value;
  router.replace({ name: 'bulkImport', params: { scope: 'inspections' } });
}
</script>
